import { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { SiteFooter } from "../../components/layout/SiteFooter";
import { SiteHeader } from "../../components/layout/SiteHeader";
import { AccountStatusPage } from "../../features/auth/AccountStatusPage";
import { AcceptInvitationPage } from "../../features/auth/AcceptInvitationPage";
import { AuthCallbackPage } from "../../features/auth/AuthCallbackPage";
import { OnboardingPage } from "../../features/auth/OnboardingPage";
import { ProtectedRoute } from "../../features/auth/RoleGuard";
import { RecoveryPage } from "../../features/auth/RecoveryPage";
import { SignInPage } from "../../features/auth/SignInPage";
import { HomePage } from "../../pages/HomePage";
import { PublicPage } from "../../pages/PublicPage";
import { NotFoundPage } from "../../pages/NotFoundPage";
import { RouteScrollManager } from "./RouteScrollManager";

const AdminLayout = lazy(() =>
  import("../../features/admin/AdminLayout").then((module) => ({ default: module.AdminLayout })),
);
const CommunicationsPage = lazy(() =>
  import("../../features/admin/CommunicationsPage").then((module) => ({
    default: module.CommunicationsPage,
  })),
);
const managementPages = () => import("../../features/admin/ManagementPages");
const PeopleManagementPage = lazy(() =>
  managementPages().then((module) => ({ default: module.PeopleManagementPage })),
);
const SkyCatalogManagementPage = lazy(() =>
  managementPages().then((module) => ({ default: module.SkyCatalogManagementPage })),
);
const IgnitionManagementPage = lazy(() =>
  managementPages().then((module) => ({ default: module.IgnitionManagementPage })),
);
const operationsPages = () => import("../../features/admin/OperationsPages");
const AdminDashboardPage = lazy(() =>
  operationsPages().then((module) => ({ default: module.AdminDashboardPage })),
);
const ProgramsEventsPage = lazy(() =>
  operationsPages().then((module) => ({ default: module.ProgramsEventsPage })),
);
const ServiceAdminPage = lazy(() =>
  operationsPages().then((module) => ({ default: module.ServiceAdminPage })),
);
const ImpactMilestonesAdminPage = lazy(() =>
  import("../../features/admin/ImpactMilestonesAdminPage").then((module) => ({
    default: module.ImpactMilestonesAdminPage,
  })),
);
const reportingPages = () => import("../../features/admin/ReportingPages");
const ReportsPage = lazy(() =>
  reportingPages().then((module) => ({ default: module.ReportsPage })),
);
const SettingsPage = lazy(() =>
  reportingPages().then((module) => ({ default: module.SettingsPage })),
);
const AsterAdminPage = lazy(() =>
  reportingPages().then((module) => ({ default: module.AsterAdminPage })),
);
const StoriesAdminPage = lazy(() =>
  reportingPages().then((module) => ({ default: module.StoriesAdminPage })),
);
const AtlasLayout = lazy(() =>
  import("../../features/atlas/AtlasLayout").then((module) => ({ default: module.AtlasLayout })),
);
const atlasPages = () => import("../../features/atlas/AtlasPages");
const AtlasDashboardPage = lazy(() =>
  atlasPages().then((module) => ({ default: module.AtlasDashboardPage })),
);
const MyServicePage = lazy(() =>
  atlasPages().then((module) => ({ default: module.MyServicePage })),
);
const ProgramsPage = lazy(() => atlasPages().then((module) => ({ default: module.ProgramsPage })));
const EventsPage = lazy(() => atlasPages().then((module) => ({ default: module.EventsPage })));
const ProfilePage = lazy(() => atlasPages().then((module) => ({ default: module.ProfilePage })));
const ReflectionsPage = lazy(() =>
  atlasPages().then((module) => ({ default: module.ReflectionsPage })),
);
const TimelinePage = lazy(() => atlasPages().then((module) => ({ default: module.TimelinePage })));
const communityPages = () => import("../../features/atlas/AtlasCommunityPages");
const MemberDirectoryPage = lazy(() =>
  communityPages().then((module) => ({ default: module.MemberDirectoryPage })),
);
const ResourceLibraryPage = lazy(() =>
  communityPages().then((module) => ({ default: module.ResourceLibraryPage })),
);
const milestonePages = () => import("../../features/atlas/MilestonePages");
const MilestonesPage = lazy(() =>
  milestonePages().then((module) => ({ default: module.MilestonesPage })),
);
const AtlasImpactPage = lazy(() =>
  milestonePages().then((module) => ({ default: module.ImpactPage })),
);
const learningPages = () => import("../../features/learning/LearningPages");
const LearningAdminPage = lazy(() =>
  learningPages().then((module) => ({ default: module.LearningAdminPage })),
);
const LearningCenterPage = lazy(() =>
  learningPages().then((module) => ({ default: module.LearningCenterPage })),
);
const ReviewQueuePage = lazy(() =>
  import("../../features/review/ReviewQueuePage").then((module) => ({
    default: module.ReviewQueuePage,
  })),
);
const directoryPages = () => import("../../features/public-site/PublicDirectoryPages");
const ImpactPage = lazy(() => directoryPages().then((module) => ({ default: module.ImpactPage })));
const StoriesPage = lazy(() =>
  directoryPages().then((module) => ({ default: module.StoriesPage })),
);
const StoryDetailPage = lazy(() =>
  directoryPages().then((module) => ({ default: module.StoryDetailPage })),
);
const PublicProfilePage = lazy(() =>
  directoryPages().then((module) => ({ default: module.PublicProfilePage })),
);
const recognitionPages = () => import("../../features/recognition/RecognitionPages");
const CertificatePage = lazy(() =>
  recognitionPages().then((module) => ({ default: module.CertificatePage })),
);
const MyRecognitionPage = lazy(() =>
  recognitionPages().then((module) => ({ default: module.MyRecognitionPage })),
);
const RecognitionAdminPage = lazy(() =>
  recognitionPages().then((module) => ({ default: module.RecognitionAdminPage })),
);
const submissionPages = () => import("../../features/submissions/SubmissionPages");
const MySubmissionsPage = lazy(() =>
  submissionPages().then((module) => ({ default: module.MySubmissionsPage })),
);
const StarLibraryPage = lazy(() =>
  submissionPages().then((module) => ({ default: module.StarLibraryPage })),
);
const SubmissionWizardPage = lazy(() =>
  submissionPages().then((module) => ({ default: module.SubmissionWizardPage })),
);
const workPages = () => import("../../features/work/WorkPages");
const MyWorkPage = lazy(() => workPages().then((module) => ({ default: module.MyWorkPage })));
const NewWorkReportPage = lazy(() =>
  workPages().then((module) => ({ default: module.NewWorkReportPage })),
);
const WorkReportPage = lazy(() =>
  workPages().then((module) => ({ default: module.WorkReportPage })),
);
const WorkAccomplishmentsPage = lazy(() =>
  workPages().then((module) => ({ default: module.WorkAccomplishmentsPage })),
);
const WorkDocumentsPage = lazy(() =>
  workPages().then((module) => ({ default: module.WorkDocumentsPage })),
);
const WorkReportsAdminPage = lazy(() =>
  import("../../features/work/WorkAdminPage").then((module) => ({
    default: module.WorkReportsAdminPage,
  })),
);
const ParentStationSponsorTrackerPage = lazy(() =>
  import("../../features/staff/ParentStationSponsorTrackerPage").then((module) => ({
    default: module.ParentStationSponsorTrackerPage,
  })),
);

const publicPaths = ["/about", "/get-involved", "/privacy", "/accessibility", "/terms"];

function CurrentPublicPage() {
  const location = useLocation();
  return <PublicPage path={location.pathname} />;
}

function OAuthAwareHomePage() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  if (params.has("code") || params.has("error") || params.has("error_description")) {
    return <Navigate to={`/auth/callback${location.search}`} replace />;
  }
  return <HomePage />;
}

export function AppRouter() {
  const location = useLocation();
  const usesApplicationChrome =
    location.pathname.startsWith("/atlas") ||
    location.pathname.startsWith("/admin") ||
    location.pathname.startsWith("/review") ||
    location.pathname.startsWith("/staff") ||
    location.pathname.startsWith("/certificate");
  return (
    <>
      <RouteScrollManager />
      {!usesApplicationChrome && <SiteHeader />}
      <main id="main-content">
        <Suspense fallback={<div className="route-loading">Loading…</div>}>
          <Routes>
            <Route path="/" element={<OAuthAwareHomePage />} />
            <Route path="/stars" element={<Navigate to="/" replace />} />
            <Route path="/stars/:slug" element={<Navigate to="/" replace />} />
            <Route path="/impact" element={<ImpactPage />} />
            <Route path="/stories" element={<StoriesPage />} />
            <Route path="/stories/:slug" element={<StoryDetailPage />} />
            <Route path="/guides/:slug" element={<PublicProfilePage />} />
            {publicPaths.map((path) => (
              <Route key={path} path={path} element={<CurrentPublicPage />} />
            ))}
            <Route path="/sign-in" element={<SignInPage />} />
            <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route path="/onboarding" element={<OnboardingPage />} />
            <Route path="/account/recovery" element={<RecoveryPage />} />
            <Route path="/account/:status" element={<AccountStatusPage />} />
            <Route
              path="/atlas"
              element={
                <ProtectedRoute>
                  <AtlasLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<AtlasDashboardPage />} />
              <Route path="sky" element={<StarLibraryPage />} />
              <Route path="stars" element={<StarLibraryPage />} />
              <Route path="star-library" element={<StarLibraryPage />} />
              <Route path="programs" element={<ProgramsPage />} />
              <Route path="events" element={<EventsPage />} />
              <Route path="work" element={<MyWorkPage />} />
              <Route path="work/new" element={<NewWorkReportPage />} />
              <Route path="work/reports/:reportId" element={<WorkReportPage />} />
              <Route path="work/accomplishments" element={<WorkAccomplishmentsPage />} />
              <Route path="work/documents" element={<WorkDocumentsPage />} />
              <Route path="submissions" element={<MySubmissionsPage />} />
              <Route path="submissions/new" element={<SubmissionWizardPage />} />
              <Route path="service" element={<MyServicePage />} />
              <Route path="milestones" element={<MilestonesPage />} />
              <Route path="impact" element={<AtlasImpactPage />} />
              <Route path="timeline" element={<TimelinePage />} />
              <Route path="reflections" element={<ReflectionsPage />} />
              <Route path="people" element={<MemberDirectoryPage />} />
              <Route path="resources" element={<ResourceLibraryPage />} />
              <Route path="recognition" element={<MyRecognitionPage />} />
              <Route path="learning" element={<LearningCenterPage />} />
              <Route path="profile" element={<ProfilePage />} />
            </Route>
            <Route
              path="/admin"
              element={
                <ProtectedRoute
                  allowedRoles={["program_director", "administrator", "super_administrator"]}
                >
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<AdminDashboardPage />} />
              <Route
                path="accounts"
                element={
                  <ProtectedRoute allowedRoles={["administrator", "super_administrator"]}>
                    <PeopleManagementPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="catalog"
                element={
                  <ProtectedRoute allowedRoles={["administrator", "super_administrator"]}>
                    <SkyCatalogManagementPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="ignitions"
                element={
                  <ProtectedRoute allowedRoles={["administrator", "super_administrator"]}>
                    <IgnitionManagementPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="learning"
                element={
                  <ProtectedRoute allowedRoles={["administrator", "super_administrator"]}>
                    <LearningAdminPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="communications"
                element={
                  <ProtectedRoute allowedRoles={["administrator", "super_administrator"]}>
                    <CommunicationsPage />
                  </ProtectedRoute>
                }
              />
              <Route path="programs" element={<ProgramsEventsPage />} />
              <Route path="service" element={<ServiceAdminPage />} />
              <Route
                path="impact"
                element={
                  <ProtectedRoute allowedRoles={["administrator", "super_administrator"]}>
                    <ImpactMilestonesAdminPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="recognition"
                element={
                  <ProtectedRoute allowedRoles={["administrator", "super_administrator"]}>
                    <RecognitionAdminPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="stories"
                element={
                  <ProtectedRoute allowedRoles={["administrator", "super_administrator"]}>
                    <StoriesAdminPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="reports"
                element={
                  <ProtectedRoute allowedRoles={["administrator", "super_administrator"]}>
                    <ReportsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="work-reports"
                element={
                  <ProtectedRoute allowedRoles={["administrator", "super_administrator"]}>
                    <WorkReportsAdminPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="aster"
                element={
                  <ProtectedRoute allowedRoles={["administrator", "super_administrator"]}>
                    <AsterAdminPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="settings"
                element={
                  <ProtectedRoute allowedRoles={["administrator", "super_administrator"]}>
                    <SettingsPage />
                  </ProtectedRoute>
                }
              />
            </Route>
            <Route
              path="/staff/parent-station"
              element={
                <ProtectedRoute>
                  <ParentStationSponsorTrackerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/review/submissions"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    "coordinator",
                    "program_director",
                    "administrator",
                    "super_administrator",
                  ]}
                >
                  <ReviewQueuePage />
                </ProtectedRoute>
              }
            />
            <Route path="/certificate/:verificationId" element={<CertificatePage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>
      {!usesApplicationChrome && <SiteFooter />}
    </>
  );
}
